import { API_CONFIG, API_ENDPOINTS, getHeaders, getHeadersWithOrg, apiCall, authFetch } from './client';
import i18n from '../../i18n';
import {
  ErrorResponse,
  UserAclResponse,
  EnsureUserResponse,
  AcceptTermsResponse,
  OrgRoleDefinition,
  OrgRolesListResponse,
  CreateRoleRequest,
  UpdateRoleRequest,
  OrgUsersListResponse,
  OrgUsersQueryParams,
  AddMultiSiteRoleRequest,
  ReplaceUserRolesRequest,
  PermissionCatalogEntry,
  PermissionCatalogResponse,
} from '../../types';

// =============================================================================
// RBAC / Permission API Functions
// =============================================================================

/**
 * Fetch the current user's ACL (roles + permissions) for an org, optionally scoped to a site.
 * Returns null on 404 (no roles).
 */
export async function fetchMyAcl(
  orgId: string,
  siteId?: string | null,
): Promise<UserAclResponse | null> {
  let url = `${API_CONFIG.BASE_URL}${API_ENDPOINTS.ORG_MY_ACL(orgId)}`;
  if (siteId) {
    url += `?siteId=${encodeURIComponent(siteId)}`;
  }

  try {
    const response = await authFetch(url, {
      method: 'GET',
      headers: getHeaders(),
    });

    if (response.status === 404) {
      return null;
    }

    if (!response.ok) {
      const errorData: ErrorResponse = await response.json();
      throw new Error(errorData.detail || errorData.error || `Failed to fetch ACL: ${response.status}`);
    }

    return await response.json();
  } catch (error) {
    if (error instanceof Error) throw error;
    throw new Error(i18n.t('errors.rbac.aclFetchFailed'));
  }
}

/**
 * Remove ALL roles for a user in an org (requires org:manage:user).
 */
export async function removeOrgUser(
  orgId: string,
  userId: string,
): Promise<void> {
  const url = `${API_CONFIG.BASE_URL}${API_ENDPOINTS.ORG_USER_ACL(orgId, userId)}`;

  try {
    const response = await authFetch(url, {
      method: 'DELETE',
      headers: getHeadersWithOrg(),
    });

    if (!response.ok) {
      const errorData: ErrorResponse = await response.json();
      throw new Error(errorData.detail || errorData.error || `Failed to remove user: ${response.status}`);
    }
  } catch (error) {
    if (error instanceof Error) throw error;
    throw new Error(i18n.t('errors.rbac.userRemoveFailed'));
  }
}

/**
 * Replace all role assignments for a user (requires org:manage:user).
 */
export async function editUserRoles(
  orgId: string,
  userId: string,
  body: ReplaceUserRolesRequest,
): Promise<{ status: string; user_id: string; assigned: unknown[]; skipped: unknown[] }> {
  return apiCall<{ status: string; user_id: string; assigned: unknown[]; skipped: unknown[] }>(
    `${API_CONFIG.BASE_URL}${API_ENDPOINTS.ORG_USER_ROLES(orgId, userId)}`,
    {
      method: 'PUT',
      headers: {
        ...getHeadersWithOrg(),
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(body),
    },
  );
}

// =============================================================================
// Org Resolution
// =============================================================================

/**
 * Fetch the current user's resolved org memberships from the backend.
 * The backend handles IMS org lookup, canonical id construction, and
 * personal-org fallback — the frontend just consumes the result.
 */
export async function fetchMyOrgs(): Promise<{ orgIds: string[]; orgNames: Record<string, string> }> {
  const url = `${API_CONFIG.BASE_URL}${API_ENDPOINTS.MY_ORGS}`;
  const data = await apiCall<{ orgs: Array<{ org_id: string; org_name: string }> }>(url, {
    method: 'GET',
    headers: getHeaders(),
  });

  const orgIds: string[] = [];
  const orgNames: Record<string, string> = {};
  for (const entry of data.orgs) {
    orgIds.push(entry.org_id);
    orgNames[entry.org_id] = entry.org_name;
    const at = entry.org_id.lastIndexOf('@');
    if (at > 0) {
      orgNames[entry.org_id.slice(0, at)] = entry.org_name;
    }
  }
  return { orgIds, orgNames };
}

// =============================================================================
// User Management
// =============================================================================

/**
 * Ensure the current user has a Flameback __USER__ node for the given org.
 * Called after IMS authentication on every app entry. Idempotent (always 200).
 */
export async function ensureUser(orgId: string): Promise<EnsureUserResponse> {
  return apiCall<EnsureUserResponse>(
    `${API_CONFIG.BASE_URL}${API_ENDPOINTS.ENSURE_USER(orgId)}`,
    {
      method: 'POST',
      headers: getHeaders(),
    },
  );
}

/**
 * Record that the current user accepted the terms and conditions for the given org.
 * The __USER__ node must already exist (call ensureUser first).
 */
export async function acceptTerms(orgId: string): Promise<AcceptTermsResponse> {
  return apiCall<AcceptTermsResponse>(
    `${API_CONFIG.BASE_URL}${API_ENDPOINTS.ACCEPT_TERMS(orgId)}`,
    {
      method: 'POST',
      headers: getHeaders(),
    },
  );
}

// =============================================================================
// Org-Level Role CRUD
// =============================================================================

export async function fetchOrgRoles(orgId: string): Promise<OrgRoleDefinition[]> {
  const url = `${API_CONFIG.BASE_URL}${API_ENDPOINTS.ORG_ROLES(orgId)}`;

  try {
    const response = await authFetch(url, {
      method: 'GET',
      headers: getHeaders(),
    });

    if (!response.ok) {
      const errorData: ErrorResponse = await response.json();
      throw new Error(errorData.detail || errorData.error || `Failed to fetch org roles: ${response.status}`);
    }

    const data: OrgRolesListResponse = await response.json();
    return data.roles || [];
  } catch (error) {
    if (error instanceof Error) throw error;
    throw new Error(i18n.t('errors.rbac.orgRolesFetchFailed'));
  }
}

export async function createOrgRole(
  orgId: string,
  body: CreateRoleRequest,
): Promise<OrgRoleDefinition> {
  return apiCall<OrgRoleDefinition>(
    `${API_CONFIG.BASE_URL}${API_ENDPOINTS.ORG_ROLES(orgId)}`,
    {
      method: 'POST',
      headers: getHeaders(),
      body: JSON.stringify(body),
    },
  );
}

export async function updateOrgRole(
  orgId: string,
  roleId: string,
  body: UpdateRoleRequest,
): Promise<OrgRoleDefinition> {
  return apiCall<OrgRoleDefinition>(
    `${API_CONFIG.BASE_URL}${API_ENDPOINTS.ORG_ROLE(orgId, roleId)}`,
    {
      method: 'PUT',
      headers: getHeaders(),
      body: JSON.stringify(body),
    },
  );
}

export async function deleteOrgRole(orgId: string, roleId: string): Promise<void> {
  const url = `${API_CONFIG.BASE_URL}${API_ENDPOINTS.ORG_ROLE(orgId, roleId)}`;

  try {
    const response = await authFetch(url, {
      method: 'DELETE',
      headers: getHeaders(),
    });

    if (!response.ok) {
      const errorData: ErrorResponse = await response.json();
      throw new Error(errorData.detail || errorData.error || `Failed to delete role: ${response.status}`);
    }
  } catch (error) {
    if (error instanceof Error) throw error;
    throw new Error(i18n.t('errors.rbac.roleDeleteFailed'));
  }
}

// =============================================================================
// Org-Level User Management
// =============================================================================

/**
 * Fetch one page of org members and their role assignments. Pagination and
 * filtering (role/access/search/excludeUserId) are applied server-side --
 * this never fetches the full member list to filter/paginate client-side.
 */
export async function fetchOrgUsers(
  orgId: string,
  params: OrgUsersQueryParams = {},
): Promise<OrgUsersListResponse> {
  const query = new URLSearchParams();
  query.set('page', String(params.page ?? 1));
  query.set('page_size', String(params.pageSize ?? 5));
  if (params.role) query.set('role', params.role);
  if (params.access) query.set('access', params.access);
  if (params.search) query.set('search', params.search);
  if (params.excludeUserId) query.set('exclude_user_id', params.excludeUserId);

  const url = `${API_CONFIG.BASE_URL}${API_ENDPOINTS.ORG_USER_ROLE_LIST(orgId)}?${query.toString()}`;

  try {
    const response = await authFetch(url, {
      method: 'GET',
      headers: getHeaders(),
    });

    if (!response.ok) {
      const errorData: ErrorResponse = await response.json();
      throw new Error(errorData.detail || errorData.error || `Failed to fetch org users: ${response.status}`);
    }

    return await response.json();
  } catch (error) {
    if (error instanceof Error) throw error;
    throw new Error(i18n.t('errors.rbac.orgUsersFetchFailed'));
  }
}

export async function addMultiSiteRole(
  orgId: string,
  userId: string,
  body: AddMultiSiteRoleRequest,
): Promise<{ status: string; assigned: Array<{ role_id: string; site_id: string }>; skipped: Array<{ role_id: string; site_id: string }> }> {
  return apiCall<{ status: string; assigned: Array<{ role_id: string; site_id: string }>; skipped: Array<{ role_id: string; site_id: string }> }>(
    `${API_CONFIG.BASE_URL}${API_ENDPOINTS.ORG_USER_ROLES(orgId, userId)}`,
    {
      method: 'POST',
      headers: getHeaders(),
      body: JSON.stringify(body),
    },
  );
}

// =============================================================================
// Permission Catalog
// =============================================================================

/**
 * Fetch the static permission catalog (display names, scope, assignability).
 * Used to label permissions and to build the role editor's permission list.
 */
export async function fetchPermissionCatalog(): Promise<PermissionCatalogEntry[]> {
  const data = await apiCall<PermissionCatalogResponse>(
    `${API_CONFIG.BASE_URL}${API_ENDPOINTS.PERMISSIONS()}`,
    {
      method: 'GET',
      headers: getHeaders(),
    },
  );
  return data.permissions || [];
}
